<template>
  <v-container fluid>
    <v-select
      v-model="selectedServices"
      label="Select services"
      :items="availableServices"
      dense
      multiple
    ></v-select>
    <v-expansion-panels tile flat multiple>
      <v-expansion-panel v-for="(service, serviceName) in value" :key="serviceName">
        <template v-if="serviceName === 'reserves'">
          <v-expansion-panel-header>Reserves</v-expansion-panel-header>
          <v-expansion-panel-content class="px-0">
            <Aggregate
              title="Reserve Services"
              v-model="computedValue.reserves"
              :ItemConstructor="Services.Service.Reserve"
              :headers="[
                { text: 'Reserve linked', value: 'projectReserveId' },
                { text: 'Active', value: 'active' },
              ]"
              :disabled="addReservesDisabled"
              @change="change"
            >
              <template #[`item.active`]="{ item }">
                <v-switch v-model="item.active" dense @change="$emit('change')"></v-switch>
              </template>
              <template #[`item.projectReserveId`]="{ item }">
                <v-select
                  v-model="item.projectReserveId"
                  item-text="name"
                  item-value="id"
                  :items="availableProjectReserves(item)"
                  @change="reserveAssign($event, item)"
                />
              </template>
              <template #default="{ item }">
                <v-row
                  v-for="(param, paramName) in serviceParams('reserves')"
                  :key="paramName"
                  class="px-8"
                  dense
                >
                  <v-col cols="12">
                    <InputServiceParam
                      v-if="!(paramName.includes('constant') || paramName.includes('profile'))"
                      v-model="item[paramName]"
                      :label="param.title"
                      :num_years="num_years"
                      :year_start="year_start"
                      :suffix="getSchemaProperty('reserves', paramName, 'unit') || ''"
                      :input-type="getInputComponent('reserves', paramName)"
                      :items="param.enum || []"
                      :profile-type="getSchemaProperty('reserves', paramName, 'profileType') || ''"
                      :auto-allocation="param.autoAllocation"
                      :nullable="param.nullable || false"
                      @change="$emit('change')"
                    ></InputServiceParam>
                  </v-col>
                </v-row>
              </template>
            </Aggregate>
          </v-expansion-panel-content>
        </template>
        <span v-else-if="serviceName !== 'reserves'">
          <v-expansion-panel-header
            :class="`${service.active ? '' : 'text--disabled'} text-subtitle-1 font-weight-medium`"
          >
            <v-row no-gutters>
              <v-col class="shrink mr-4" v-if="Object.keys(service).includes('active')">
                <v-switch
                  v-model="service.active"
                  dense
                  class="my-0 pt-0"
                  :height="25"
                  @click.native.stop=""
                  @change="$emit('change')"
                  hide-details
                />
              </v-col>
              <v-col>
                {{ `${service.name} ${service.active ? "(active)" : ""}` }}
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card
              v-for="(param, paramName) in serviceParams(serviceName)"
              :key="paramName"
              dense
              flat
              class="grow"
            >
              <template v-if="paramName === 'renewables'">
                <Aggregate
                  :title="param.title"
                  v-model="service[paramName]"
                  :ItemConstructor="
                    Services[camelToPascal(serviceName)][
                      getSchemaProperty(serviceName, paramName, 'constructor')
                    ]
                  "
                  :headers="[{ text: 'Generator linked', value: 'generatorId' }]"
                  no-expand
                >
                  <template #[`item.generatorId`]="{ item }">
                    <v-select
                      v-model="item.generatorID"
                      label="Generator"
                      :items="projectGenerators"
                      item-value="id"
                      item-text="name"
                      @change="$emit('change')"
                    ></v-select>
                  </template>
                  <template #default="{ item }">
                    <v-row
                      v-for="(param, paramName) in serviceParams('renewableItem')"
                      :key="paramName"
                      dense
                    >
                      <InputServiceParam
                        v-if="!(paramName.includes('constant') || paramName.includes('profile'))"
                        v-model="item[paramName]"
                        :label="param.title"
                        :suffix="getSchemaProperty('renewableItem', paramName, 'unit') || ''"
                        :num_years="num_years"
                        :year_start="year_start"
                        :input-type="getInputComponent('renewableItem', paramName)"
                        :auto-allocation="param.autoAllocation"
                        :profile-type="
                          getSchemaProperty('renewableItem', paramName, 'profileType') || ''
                        "
                        :nullable="
                          getSchemaProperty('renewableItem', paramName, 'nullable') || false
                        "
                        @change="$emit('change')"
                      />
                    </v-row>
                  </template>
                </Aggregate>
              </template>

              <template v-if="paramName === 'demandID'">
                <v-select
                  v-model="service[paramName]"
                  label="Demand"
                  :items="projectDemands"
                  item-value="id"
                  item-text="name"
                  @change="$emit('change')"
                ></v-select>
              </template>

              <InputServiceParam
                v-else
                v-model="service[paramName]"
                :label="param.title"
                :suffix="getSchemaProperty(serviceName, paramName, 'unit') || ''"
                :num_years="num_years"
                :year_start="year_start"
                :input-type="getInputComponent(serviceName, paramName)"
                :tooltip="getSchemaProperty(serviceName, paramName, 'tooltip') || ''"
                :items="param.enum || []"
                :auto-allocation="param.autoAllocation"
                :profile-type="getSchemaProperty(serviceName, paramName, 'profileType') || ''"
                :nullable="getSchemaProperty(serviceName, paramName, 'nullable') || false"
                :max="getSchemaProperty(serviceName, paramName, 'maximum') || ''"
                :min="getSchemaProperty(serviceName, paramName, 'minimum') || ''"
                @change="$emit('change')"
              />
            </v-card>
          </v-expansion-panel-content>
        </span>
        <v-divider></v-divider>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import { schemas, Services } from "data-dictionary";
import { camelCase, camelToTitleCase, titleCase, camelToPascal } from "@/plugins/utils";

export default {
  name: "SystemProjectServices",
  components: {
    InputServiceParam: () => import("@/components/InputServiceParam.vue"),
    Aggregate: () => import("@/components/Aggregate.vue"),
  },
  mixins: [],
  props: {
    value: { type: Object, required: true },
    year_start: { type: Number, required: true },
    num_years: { type: Number, required: true },
    projectReserves: { type: Array, required: false },
    projectGenerators: { type: Array, required: false },
    projectDemands: { type: Array, required: true },
    resourceType: { type: String, required: true },
    servicesSchema: { type: Object, required: false, default: null },
  },
  data() {
    return { Services, nullableStates: {} };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
    servicesSchemas() {
      const computedSchemas = schemas
        .filter((item) => Array.isArray(item))
        .flat(1)
        .reduce(
          (obj, key) => ({
            ...obj,
            [this.camelCase(
              key.$id.replace(".schema.json", "").replace("service", "").replace(/_/g, " ")
            )]: key,
          }),
          {}
        );

      computedSchemas.ESOCManagement = computedSchemas.esocManagement;

      return computedSchemas;
    },
    availableServices() {
      return Object.keys(
        this.servicesSchema ||
          schemas.find(
            (item) =>
              !Array.isArray(item) && item.$id === `${this.resourceType}_project.schema.json`
          ).properties.services.properties
      ).map((service) => ({
        text: service === "ESOCManagement" ? "SoC Management" : camelToTitleCase(service),
        value: service,
      }));
    },
    selectedServices: {
      get() {
        return Object.keys(this.value);
      },
      set(val) {
        const newVal = {
          ...val
            .filter((key) => key !== "reserves")
            .reduce(
              (acc, key) => ({
                ...acc,
                [key]: new this.Services[`${key.charAt(0).toUpperCase() + key.slice(1)}`](),
              }),
              {}
            ),
          ...(val.includes("reserves") ? { reserves: [] } : {}),
          ...this.value,
        };
        Object.keys(this.value).forEach((key) => {
          if (!val.includes(key)) {
            delete newVal[key];
          }
        });
        this.$emit("input", newVal);
      },
    },

    addReservesDisabled() {
      return this.value.reserves.length >= this.projectReserves.length;
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    camelCase,
    camelToTitleCase,
    titleCase,
    camelToPascal,
    serviceParams(service) {
      if (service !== "reserves") {
        const { name, active, ...serviceParams } = {
          ...(this.servicesSchemas[service].allOf ? this.servicesSchemas.general.properties : {}),
          ...this.servicesSchemas[service].properties,
        };
        return serviceParams;
      }
      const { name, active, ...serviceParams } = {
        ...(this.servicesSchemas[service].items.allOf
          ? this.servicesSchemas.general.properties
          : {}),
        ...this.servicesSchemas[service].items.properties,
      };
      return serviceParams;
    },
    getInputComponent(serviceName, paramName) {
      const type = this.getSchemaProperty(serviceName, paramName, "type");
      const inputType = this.getSchemaProperty(serviceName, paramName, "inputType");
      if (inputType) return inputType;

      if (type === "boolean") {
        return "switch";
      }
      return "switch";
    },
    getSchemaProperty(serviceName, paramName, property) {
      if (serviceName === "reserves") {
        if (this.servicesSchemas[serviceName].items.properties[paramName]) {
          return this.servicesSchemas[serviceName].items.properties[paramName][property] || null;
        }
        return this.servicesSchemas.general.properties[paramName][property || null];
      }
      // if paramName is specific for service
      if (this.servicesSchemas[serviceName].properties[paramName]) {
        return this.servicesSchemas[serviceName].properties[paramName][property]
          ? this.servicesSchemas[serviceName].properties[paramName][property]
          : null;
      }
      // paramName is general
      return this.servicesSchemas.general.properties[paramName][property]
        ? this.servicesSchemas.general.properties[paramName][property]
        : null;
    },
    change() {
      this.$emit("input", this.value);
      this.$emit("change");
    },
    reserveAssign($event, item) {
      // eslint-disable-next-line no-param-reassign
      item.name = this.projectReserves.find((reserve) => reserve.id === $event).name;
      this.$emit("input", this.value);
      this.$emit("change");
    },
    availableProjectReserves(reserve) {
      return [
        { name: "- Select Reserve -", id: null },
        ...this.projectReserves.filter(
          (projectReserve) =>
            !this.value.reserves.map((r) => r.projectReserveId).includes(projectReserve.id)
        ),
        ...(reserve.projectReserveId
          ? [this.projectReserves.find((r) => r.id === reserve.projectReserveId)]
          : []),
      ];
    },
  },
};
</script>

<style></style>
